import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDialogLab } from '../../lab/hooks/useDialogLab';
import { useQuery } from '@tanstack/react-query';
import {
  X, Plus, Search, Trash2, UserCheck, FileText, Clock, RefreshCw, Download, PartyPopper,
  User, Camera, ContactRound,
} from 'lucide-react';
import { AlunoEditSidePanel } from './AlunoEditSidePanel';
import { AlunoEnderecoMap } from './AlunoEnderecoMap';
import { ListAvatar, hasFotoReal } from '../admin/list-avatar';
import { Modal } from '../ui/Modal';
import { AvatarCropper } from '../ui/AvatarCropper';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { StyledQrCode } from '../../security/qr/StyledQrCode';
import { useCrud } from '../../api/hooks/useCrud';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import type { Aluno, Turma } from '../../types/models';
import { validateCPF, formatCPF, formatPhone, formatCEP, stripMask } from '../../utils/cpfUtils';
import { formatDateBR } from '../../utils/dateUtils';
import '../../components/ui/datatable.css';
import { ROUTES } from '../../api/routes';
import { UPLOAD } from '../../api/routes';

interface ResponsavelItem {
  id?: string;
  nome: string;
  email: string;
  telefone: string;
  cpf: string;
  dataNascimento: string;
  parentesco: string;
  principal: boolean;
  fotoUrl?: string | null;
  fromSearch?: boolean;
}

/** Linha resumo para lista compacta de responsáveis vinculados */
function respResumoMeta(r: ResponsavelItem): string {
  const parts: string[] = [];
  if (r.parentesco) parts.push(r.parentesco);
  if (r.cpf) parts.push(r.cpf);
  if (r.telefone) parts.push(r.telefone);
  if (r.email) parts.push(r.email);
  return parts.join(' · ') || 'Sem contato cadastrado';
}

const STEPS = ['Dados Pessoais', 'Acadêmico', 'Endereço', 'Saúde', 'Responsável', 'RFID'];

/** Props do formulário multi-step de aluno */
export interface AlunoFormProps {
  open: boolean;
  onClose: () => void;
  editing?: Aluno | null;
  onCreated?: (id: string) => void;
  initialResponsavel?: ResponsavelItem | null;
}

const emptyForm = {
  nome: '', dataNascimento: '', genero: 'MASCULINO', cpf: '',
  matricula: '', turmaId: '',
  consentimentoLGPD: 'false' as string,
  rfidCodigo: '',
  // Campos MEC (Sprint 2)
  corRaca: '', nacionalidade: 'BRASILEIRO_NATO', paisOrigem: '', nis: '',
  // Endereço (Sprint 4)
  cep: '', endereco: '', numero: '', bairro: '', cidade: '', uf: '', complemento: '',
  // Saúde (Sprint 4)
  alergias: '', tipoSanguineo: '', restricaoAlimentar: '', planoSaude: '',
  tipoDeficiencia: '', deficienciaDetalhe: '',
};

const emptyResp: ResponsavelItem = {
  nome: '', email: '', telefone: '', cpf: '', dataNascimento: '', parentesco: '', principal: true,
};

function pickRfidCodigo(a: any): string {
  const tags = a.tags;
  if (!Array.isArray(tags) || !tags.length) return '';
  const active = tags.find((t: any) => t.ativa);
  return (active || tags[0])?.codigo || '';
}

/** Campos do wizard a partir do objeto aluno (lista ou GET detalhe) */
function formFieldsFromAluno(a: any) {
  return {
    nome: a.nome || '', dataNascimento: a.dataNascimento?.split('T')[0] || '',
    genero: a.genero || 'MASCULINO', cpf: a.cpf ? formatCPF(a.cpf) : '',
    matricula: a.matricula || '', turmaId: a.turmaId || '',
    consentimentoLGPD: a.consentimentoLGPD ? 'true' : 'false',
    rfidCodigo: pickRfidCodigo(a),
    corRaca: a.corRaca || '', nacionalidade: a.nacionalidade || 'BRASILEIRO_NATO',
    paisOrigem: a.paisOrigem || '', nis: a.nis || '',
    cep: a.cep || '', endereco: a.endereco || '',
    numero: a.numero || '',
    bairro: a.bairro || '', cidade: a.cidade || '',
    uf: a.uf || '', complemento: a.complemento || '',
    alergias: a.alergias || '', tipoSanguineo: a.tipoSanguineo || '',
    restricaoAlimentar: a.restricaoAlimentar || '', planoSaude: a.planoSaude || '',
    tipoDeficiencia: a.tipoDeficiencia || '', deficienciaDetalhe: a.deficienciaDetalhe || '',
  };
}

/** Verifica se um responsável corresponde à query de busca de forma flexível e precisa */
function matchResponsavel(r: any, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const cleanDigits = q.replace(/\D/g, '');
  const nome = (r.nome || '').toLowerCase();
  const email = (r.email || '').toLowerCase();
  const parentesco = (r.parentesco || '').toLowerCase();
  const cpf = (r.cpf || '').replace(/\D/g, '');
  const telefone = (r.telefone || '').replace(/\D/g, '');
  const filhosNomes = (r.alunos || [])
    .map((a: any) => (a.aluno?.nome || '').toLowerCase())
    .join(' ');

  return (
    nome.includes(q) ||
    email.includes(q) ||
    parentesco.includes(q) ||
    filhosNomes.includes(q) ||
    (cleanDigits.length > 0 && (cpf.includes(cleanDigits) || telefone.includes(cleanDigits)))
  );
}

/** Responsáveis no formato do wizard — relação ResponsavelAluno ou campos legados no Aluno */
function responsaveisFromAluno(a: any): ResponsavelItem[] {
  const list = a.responsaveis;
  if (list && list.length > 0) {
    return list.map((ra: any) => {
      const r = ra.responsavel;
      const tel = r?.telefone != null && r.telefone !== '' ? formatPhone(String(r.telefone)) : '';
      const isPrincipal = !!ra.principal;
      const legacyCpf = isPrincipal && a.cpfResponsavel ? formatCPF(String(a.cpfResponsavel)) : '';
      return {
        id: r?.id,
        nome: r?.nome || '',
        email: r?.email || '',
        telefone: tel,
        cpf: r?.cpf ? formatCPF(String(r.cpf)) : (isPrincipal && a.cpfResponsavel ? formatCPF(String(a.cpfResponsavel)) : ''),
        dataNascimento: r?.dataNascimento ? new Date(r.dataNascimento).toISOString().slice(0, 10) : '',
        parentesco: ra.grauParentesco || '',
        principal: !!ra.principal,
        fotoUrl: r?.fotoUrl ?? null,
        fromSearch: true,
      };
    });
  }
  if (a.nomeResponsavel) {
    return [{
      nome: a.nomeResponsavel,
      email: a.emailResponsavel || '',
      telefone: a.telefoneResponsavel ? formatPhone(String(a.telefoneResponsavel)) : '',
      cpf: a.cpfResponsavel ? formatCPF(String(a.cpfResponsavel)) : '',
      dataNascimento: '',
      parentesco: a.parentesco || '',
      principal: true,
    }];
  }
  return [{ ...emptyResp }];
}

/** Rascunho salvo em localStorage */
interface Draft {
  id: string;
  nome: string; // nome do aluno (para listagem)
  step: number;
  form: typeof emptyForm;
  responsaveis: ResponsavelItem[];
  filtroTurno: string;
  filtroSerie: string;
  updatedAt: string;
}

const DRAFTS_KEY = 'ac_matricula_drafts';

const SERIE_LABELS: Record<string, string> = {
  '1_ANO': '1º Ano',
  '2_ANO': '2º Ano',
  '3_ANO': '3º Ano',
  '4_ANO': '4º Ano',
  '5_ANO': '5º Ano',
  '6_ANO': '6º Ano',
  '7_ANO': '7º Ano',
  '8_ANO': '8º Ano',
  '9_ANO': '9º Ano',
  '1_SERIE': '1ª Série',
  '2_SERIE': '2ª Série',
  '3_SERIE': '3ª Série',
  CRECHE: 'Creche',
  PRE_I: 'Pré I',
  PRE_II: 'Pré II',
  EJA_I: 'EJA I',
  EJA_II: 'EJA II',
  OUTRO: 'Outro',
};

export function AlunoForm({ open, onClose, editing, onCreated, initialResponsavel }: AlunoFormProps) {
  // — Lab: registra o modal no sistema de roteiros contextuais
  // NOTA: A branch 'editing' usa <Modal dialogId="modal-ficha-aluno">, que registra internamente.
  // Este hook cobre apenas o overlay manual da branch '!editing' (novo aluno).
  useDialogLab(!editing ? 'modal-novo-aluno' : null, open && !editing);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...emptyForm });
  const [cpfError, setCpfError] = useState('');
  const [responsaveis, setResponsaveis] = useState<ResponsavelItem[]>([{ ...emptyResp }]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [showNewRespForm, setShowNewRespForm] = useState(false);
  const [newRespForm, setNewRespForm] = useState({
    nome: '',
    cpf: '',
    dataNascimento: '',
    email: '',
    telefone: '',
    parentesco: 'PAI',
    principal: false,
  });
  const [newRespErrors, setNewRespErrors] = useState<Record<string, string>>({});
  const [checkingNewResp, setCheckingNewResp] = useState(false);
  const [existingRespFound, setExistingRespFound] = useState<any | null>(null);
  const [qrToken, setQrToken] = useState<string | null>(null);
  const [filtroTurno, setFiltroTurno] = useState('');
  const [filtroSerie, setFiltroSerie] = useState('');
  const [draftId, setDraftId] = useState<string | null>(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const [successData, setSuccessData] = useState<any>(null);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestSearchQueryRef = useRef<string>('');
  const authEscolaId = useAuthStore((s) => s.escolaId || s.user?.escolaId);
  const escolaId = authEscolaId || editing?.escolaId || (editing as any)?.escola?.id;
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const [allResponsaveisList, setAllResponsaveisList] = useState<any[]>([]);
  const [hasLoadedInitialResponsaveis, setHasLoadedInitialResponsaveis] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const { create, update } = useCrud<Aluno>('alunos');
  const { data: turmas } = useCrud<Turma>('turmas');
  const { data: turnos = [] } = useCrud<any>('turnos');

  const [fotoPendente, setFotoPendente] = useState<string | null>(null);
  const [fotoBase64Criar, setFotoBase64Criar] = useState<string | null>(null);
  const [fotoUploading, setFotoUploading] = useState(false);
  const [avatarCropFile, setAvatarCropFile] = useState<File | null>(null);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);

  const { data: alunoDetalhe, isFetching: loadingAlunoDetalhe } = useQuery({
    queryKey: ['aluno-form-detail', escolaId, editing?.id],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.alunos.findOne(escolaId!, editing!.id));
      return data as Aluno;
    },
    enabled: !!open && !!editing?.id && !!escolaId,
    staleTime: 30_000,
  });

  const fetchResponsaveis = useCallback(async (q?: string) => {
    if (!escolaId) return;
    const currentQuery = q || '';
    latestSearchQueryRef.current = currentQuery;
    setSearching(true);
    try {
      const { data } = await api.get(ROUTES.responsaveis.listar(escolaId) + '/search', {
        params: currentQuery ? { q: currentQuery } : undefined,
      });
      // Descartar resposta caso o usuário já tenha digitado uma nova query
      if (latestSearchQueryRef.current !== currentQuery) {
        return;
      }
      const list = Array.isArray(data) ? data : [];
      const filtered = currentQuery ? list.filter(r => matchResponsavel(r, currentQuery)) : list;
      setSearchResults(filtered);
      if (!currentQuery) {
        setAllResponsaveisList(list);
        setHasLoadedInitialResponsaveis(true);
      }
    } catch (err) {
      console.error('Erro ao buscar responsáveis:', err);
      if (latestSearchQueryRef.current === currentQuery) {
        setSearchResults([]);
      }
    } finally {
      if (latestSearchQueryRef.current === currentQuery) {
        setSearching(false);
      }
    }
  }, [escolaId]);

  /** Resultados exibidos estritamente alinhados com o filtro digitado */
  const displayedSearchResults = useMemo(() => {
    if (!searchQuery.trim()) return searchResults;
    return searchResults.filter(r => matchResponsavel(r, searchQuery));
  }, [searchResults, searchQuery]);

  // Carrega responsáveis ao abrir ou entrar na etapa 4 (Responsável)
  useEffect(() => {
    if (step === 4 && escolaId && !hasLoadedInitialResponsaveis) {
      fetchResponsaveis();
    }
  }, [step, escolaId, hasLoadedInitialResponsaveis, fetchResponsaveis]);

  // Fechar dropdown ao clicar fora do componente de busca
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) {
        setSearchDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!open || !editing?.id || !alunoDetalhe || alunoDetalhe.id !== editing.id) return;
    setForm(formFieldsFromAluno(alunoDetalhe));
    setResponsaveis(responsaveisFromAluno(alunoDetalhe));
  }, [editing?.id, alunoDetalhe, open]);

  useEffect(() => {
    if (!open || !editing?.id) {
      setFotoPendente(null);
      setFotoBase64Criar(null);
      setAvatarCropFile(null);
    }
  }, [open, editing?.id]);

  const modalFotoSrc = fotoPendente || fotoBase64Criar || alunoDetalhe?.fotoUrl || editing?.fotoUrl || null;

  const onAvatarFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande (máx. 5 MB).');
      return;
    }
    setAvatarCropFile(file);
    e.target.value = '';
  };

  const onAvatarCropped = async (fotoBase64: string) => {
    setAvatarCropFile(null);
    if (!editing?.id) {
      setFotoBase64Criar(fotoBase64);
      return;
    }
    setFotoUploading(true);
    try {
      const { data } = await api.post<{ url: string }>(UPLOAD.fotoAluno(editing.id), { fotoBase64 });
      setFotoPendente(data.url);
      toast.success('Foto atualizada!');
    } catch {
      toast.error('Não foi possível enviar a foto.');
    } finally {
      setFotoUploading(false);
    }
  };

  /** Lê rascunhos do localStorage */
  const getDrafts = useCallback((): Draft[] => {
    try { return JSON.parse(localStorage.getItem(DRAFTS_KEY) || '[]'); } catch { return []; }
  }, []);

  /** Salva lista de rascunhos */
  const saveDrafts = useCallback((drafts: Draft[]) => {
    localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
  }, []);

  /** Remove um rascunho pelo id */
  const removeDraft = useCallback((id: string) => {
    saveDrafts(getDrafts().filter(d => d.id !== id));
  }, [getDrafts, saveDrafts]);

  /** Auto-save: debounced a cada mudança — só salva se houver dados reais */
  useEffect(() => {
    if (!open || editing || !draftId) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      // Só salva se tem algum dado significativo preenchido
      const hasData = form.nome || form.cpf || form.dataNascimento || form.turmaId || form.matricula || form.cep || form.rfidCodigo || responsaveis.some(r => r.nome || r.email || r.telefone);
      if (!hasData) {
        // Se não tem dados, remove rascunho existente (limpeza)
        removeDraft(draftId);
        return;
      }
      const drafts = getDrafts();
      const idx = drafts.findIndex(d => d.id === draftId);
      const draft: Draft = {
        id: draftId,
        nome: form.nome || 'Sem nome',
        step,
        form: { ...form },
        responsaveis: [...responsaveis],
        filtroTurno,
        filtroSerie,
        updatedAt: new Date().toISOString(),
      };
      if (idx >= 0) drafts[idx] = draft; else drafts.push(draft);
      saveDrafts(drafts);
    }, 800);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [form, responsaveis, step, filtroTurno, filtroSerie, open, editing, draftId, getDrafts, saveDrafts, removeDraft]);

  /** Inicia um rascunho novo */
  const startNewDraft = () => {
    setForm({ ...emptyForm });
    setResponsaveis([{ ...emptyResp }]);
    setStep(0);
    setFiltroTurno('');
    setFiltroSerie('');
    setCpfError('');
    setSearchQuery('');
    setSearchResults([]);
    setFotoBase64Criar(null);
    setDraftId(`draft_${Date.now()}`);
    setShowDrafts(false);
  };

  /** Retoma um rascunho existente */
  const resumeDraft = (draft: Draft) => {
    setForm({ ...emptyForm, ...draft.form });
    setResponsaveis(draft.responsaveis?.length ? draft.responsaveis : [{ ...emptyResp }]);
    setStep(draft.step || 0);
    setFiltroTurno(draft.filtroTurno || '');
    setFiltroSerie(draft.filtroSerie || '');
    setCpfError('');
    setSearchQuery('');
    setSearchResults([]);
    setFotoBase64Criar(null);
    setDraftId(draft.id);
    setShowDrafts(false);
  };

  /** Abrir edição: usa só `editing?.id` nas deps — se depender do objeto `editing`, re-renders do pai
   * recriam a referência, este efeito corre de novo e zera `responsaveis` *depois* do GET de detalhe. */
  useEffect(() => {
    if (editing?.id) {
      setDraftId(null);
      setShowDrafts(false);
      setStep(0);
      setCpfError('');
      setSearchQuery('');
      setSearchResults([]);
      setFiltroTurno('');
      setFiltroSerie('');
      setFotoBase64Criar(null);
      setSuccessData(null);
      setQrToken(null);
    } else if (open) {
      setSuccessData(null);
      setQrToken(null);
      setFotoBase64Criar(null);
      if (initialResponsavel) {
        setForm({ ...emptyForm });
        setResponsaveis([initialResponsavel]);
        setStep(0);
        setFiltroTurno('');
        setFiltroSerie('');
        setCpfError('');
        setSearchQuery('');
        setSearchResults([]);
        setDraftId(null);
        setShowDrafts(false);
      } else {
        const drafts = getDrafts();
        if (drafts.length > 0) {
          setShowDrafts(true);
        } else {
          startNewDraft();
        }
      }
    } else {
      setDraftId(null);
      setShowDrafts(false);
      setFotoBase64Criar(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.id, open, initialResponsavel]);

  if (!open) return null;

  const set = (k: string, v: string) => setForm(prev => ({ ...prev, [k]: v }));

  /** Aplica máscara de CPF ao digitar */
  const handleCPFChange = (value: string) => {
    const formatted = formatCPF(value);
    set('cpf', formatted);
    // Limpa o erro quando o usuário começa a corrigir
    if (cpfError) setCpfError('');
  };

  /** Valida CPF ao sair do campo */
  const handleCPFBlur = async () => {
    const raw = stripMask(form.cpf);
    if (raw.length > 0 && raw.length < 11) {
      setCpfError('CPF incompleto');
    } else if (raw.length === 11 && !validateCPF(raw)) {
      setCpfError('CPF inválido — verifique os dígitos');
    } else if (raw.length === 11) {
      setCpfError('');
      try {
        const { data } = await api.get(ROUTES.validacoes.cpf(raw));
        if (data.exists) {
          if (editing && data.nome === editing.nome) return;
          const msg = `Este CPF já está em uso pelo ${data.tipo === 'RESPONSAVEL' ? 'Responsável' : data.tipo === 'ALUNO' ? 'Aluno' : 'Usuário'}: ${data.nome}.`;
          setCpfError(msg);
          toast.error(msg);
        }
      } catch (err) {
        console.error('Erro validando CPF', err);
      }
    } else {
      setCpfError('');
    }
  };

  /** Aplica máscara de telefone para responsável */
  const handleRespPhoneChange = (idx: number, value: string) => {
    const formatted = formatPhone(value);
    updateResp(idx, 'telefone', formatted);
  };

  /** Atualiza campo de um responsável no array */
  const updateResp = (idx: number, field: string, value: string | boolean) => {
    setResponsaveis(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  /** Adiciona responsável vazio */
  const addResp = () => {
    setResponsaveis(prev => [...prev, { ...emptyResp, principal: false }]);
  };

  /** Remove responsável — se ficar vazio, adiciona um card limpo */
  const removeResp = (idx: number) => {
    setResponsaveis(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length === 0 ? [{ ...emptyResp }] : next;
    });
  };

  const handleSearchFocus = () => {
    setSearchDropdownOpen(true);
    if (!hasLoadedInitialResponsaveis || allResponsaveisList.length === 0) {
      fetchResponsaveis(searchQuery);
    } else if (searchQuery.trim()) {
      setSearchResults(allResponsaveisList.filter(r => matchResponsavel(r, searchQuery)));
    } else {
      setSearchResults(allResponsaveisList);
    }
  };

  /** Busca responsável existente — com debounce, filtro local instantâneo e proteção de concorrência */
  const handleSearch = (q: string) => {
    setSearchQuery(q);
    latestSearchQueryRef.current = q;
    setSearchDropdownOpen(true);
    if (searchTimer.current) clearTimeout(searchTimer.current);

    // Filtro instantâneo no cliente caso já tenhamos a lista em memória
    if (allResponsaveisList.length > 0) {
      const localFiltered = q.trim() ? allResponsaveisList.filter(r => matchResponsavel(r, q)) : allResponsaveisList;
      setSearchResults(localFiltered);
    }

    if (!q.trim()) {
      setSearching(false);
      return;
    }

    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      await fetchResponsaveis(q.trim());
    }, 200);
  };

  /** Seleciona responsável da busca — limpa cards vazios */
  const selectFromSearch = (found: any) => {
    const isAlreadyLinked = responsaveis.some(
      (r) => r.id === found.id || (found.cpf && stripMask(r.cpf) === stripMask(found.cpf))
    );
    if (isAlreadyLinked) {
      toast.warning(`${found.nome} já está na lista de responsáveis deste aluno.`);
      setSearchDropdownOpen(false);
      return;
    }

    const newResp: ResponsavelItem = {
      id: found.id,
      nome: found.nome,
      email: found.email || '',
      telefone: found.telefone ? formatPhone(String(found.telefone)) : '',
      cpf: found.cpf ? formatCPF(found.cpf) : '',
      dataNascimento: found.dataNascimento ? new Date(found.dataNascimento).toISOString().slice(0, 10) : '',
      parentesco: found.parentesco || 'PAI',
      principal: responsaveis.filter((r) => r.nome).length === 0,
      fromSearch: true,
      fotoUrl: found.fotoUrl ?? null,
    };

    setResponsaveis((prev) => {
      const filled = prev.filter((r) => r.nome || r.email || r.telefone);
      return [...filled, newResp];
    });
    setSearchQuery('');
    setSearchDropdownOpen(false);
    toast.success(`${found.nome} vinculado com sucesso!`);
  };

  const checkNewRespEmailLive = async (emailVal: string) => {
    const email = emailVal.trim().toLowerCase();
    if (!email) {
      setNewRespErrors((prev) => {
        const n = { ...prev };
        delete n.email;
        return n;
      });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNewRespErrors((prev) => ({ ...prev, email: 'E-mail inválido.' }));
      return;
    }
    try {
      const { data } = await api.get(ROUTES.validacoes.email(email));
      if (data.exists) {
        const msg = `Este e-mail (${email}) já está cadastrado para o ${
          data.tipo === 'RESPONSAVEL'
            ? 'Responsável'
            : data.tipo === 'ALUNO'
              ? 'Aluno'
              : 'Usuário'
        }: ${data.nome}. Não é permitido cadastrar com e-mail duplicado.`;
        setNewRespErrors((prev) => ({ ...prev, email: msg }));
        toast.warning(msg);
      } else {
        setNewRespErrors((prev) => {
          const n = { ...prev };
          delete n.email;
          return n;
        });
      }
    } catch (err) {
      console.error('Erro validando e-mail do novo responsável', err);
    }
  };

  const checkNewRespCpfLive = async (cpfVal: string) => {
    const cleanCpf = stripMask(cpfVal);
    if (!cleanCpf) {
      setNewRespErrors((prev) => {
        const n = { ...prev };
        delete n.cpf;
        return n;
      });
      return;
    }
    if (cleanCpf.length !== 11 || !validateCPF(cleanCpf)) {
      setNewRespErrors((prev) => ({ ...prev, cpf: 'CPF inválido (11 dígitos).' }));
      return;
    }
    try {
      const { data } = await api.get(ROUTES.validacoes.cpf(cleanCpf));
      if (data.exists) {
        if (data.tipo === 'RESPONSAVEL') {
          setExistingRespFound({
            id: data.id,
            nome: data.nome,
            cpf: cleanCpf,
            email: data.email || newRespForm.email || '',
            telefone: newRespForm.telefone || '',
            parentesco: newRespForm.parentesco,
            principal: newRespForm.principal,
            dataNascimento: data.dataNascimento || newRespForm.dataNascimento,
          });
        } else {
          const msg = `Este CPF já está em uso por um ${
            data.tipo === 'ALUNO' ? 'Aluno' : 'Usuário'
          }: ${data.nome}.`;
          setNewRespErrors((prev) => ({ ...prev, cpf: msg }));
          toast.warning(msg);
        }
      } else {
        setNewRespErrors((prev) => {
          const n = { ...prev };
          delete n.cpf;
          return n;
        });
      }
    } catch (err) {
      console.error('Erro validando CPF do novo responsável', err);
    }
  };

  const handleLinkNewResponsavel = async () => {
    const { nome, cpf, dataNascimento, email, telefone, parentesco, principal } = newRespForm;
    const errors: Record<string, string> = {};

    if (!nome.trim() || nome.trim().length < 3) {
      errors.nome = 'Nome do responsável é obrigatório e deve ter no mínimo 3 caracteres.';
    }
    const cleanCpf = stripMask(cpf);
    if (!cleanCpf || cleanCpf.length !== 11) {
      errors.cpf = 'CPF é obrigatório e deve conter 11 dígitos.';
    } else if (!validateCPF(cleanCpf)) {
      errors.cpf = 'CPF inválido.';
    }
    if (!dataNascimento) {
      errors.dataNascimento = 'Data de nascimento é obrigatória.';
    }

    const cleanEmail = email ? email.trim().toLowerCase() : '';
    if (cleanEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      errors.email = 'E-mail inválido.';
    }

    if (Object.keys(errors).length > 0) {
      setNewRespErrors(errors);
      const first = Object.values(errors)[0];
      toast.warning(first);
      return;
    }

    setCheckingNewResp(true);

    // 1. Validar e-mail se preenchido (não permitir e-mail duplicado)
    if (cleanEmail) {
      try {
        const { data: emailCheck } = await api.get(ROUTES.validacoes.email(cleanEmail));
        if (emailCheck.exists) {
          const msg = `Este e-mail (${cleanEmail}) já está em uso pelo ${
            emailCheck.tipo === 'RESPONSAVEL'
              ? 'Responsável'
              : emailCheck.tipo === 'ALUNO'
                ? 'Aluno'
                : 'Usuário'
          }: ${emailCheck.nome}. Não é permitido cadastrar com e-mail duplicado.`;
          setNewRespErrors((prev) => ({ ...prev, email: msg }));
          toast.warning(msg);
          setCheckingNewResp(false);
          return;
        }
      } catch (err) {
        console.error('Erro validando email', err);
      }
    }

    // 2. Validar CPF
    try {
      const { data: cpfCheck } = await api.get(ROUTES.validacoes.cpf(cleanCpf));
      if (cpfCheck.exists) {
        if (cpfCheck.tipo === 'RESPONSAVEL') {
          setExistingRespFound({
            id: cpfCheck.id,
            nome: cpfCheck.nome,
            cpf: cleanCpf,
            email: cpfCheck.email || cleanEmail || '',
            telefone: telefone || '',
            parentesco,
            principal,
            dataNascimento: cpfCheck.dataNascimento || dataNascimento,
          });
        } else {
          const msg = `Este CPF já está em uso por um ${
            cpfCheck.tipo === 'ALUNO' ? 'Aluno' : 'Usuário'
          }: ${cpfCheck.nome}.`;
          setNewRespErrors((prev) => ({ ...prev, cpf: msg }));
          toast.warning(msg);
        }
        setCheckingNewResp(false);
        return;
      }
    } catch (err) {
      console.error('Erro validando CPF', err);
    }
    setCheckingNewResp(false);

    // Adiciona o novo responsável sem ID (para o backend sincronizar e criar na gravação do aluno)
    const newResp: ResponsavelItem = {
      id: '',
      nome: nome.trim(),
      email: cleanEmail,
      telefone: telefone ? formatPhone(telefone) : '',
      cpf: formatCPF(cleanCpf),
      dataNascimento: new Date(dataNascimento).toISOString().slice(0, 10),
      parentesco,
      principal: principal || responsaveis.filter((r) => r.nome).length === 0,
    };

    setResponsaveis((prev) => {
      const filled = prev.filter((r) => r.nome);
      return [...filled, newResp];
    });

    toast.success(`Responsável ${nome} adicionado para vínculo!`);
    setShowNewRespForm(false);
    setNewRespErrors({});
    setNewRespForm({
      nome: '',
      cpf: '',
      dataNascimento: '',
      email: '',
      telefone: '',
      parentesco: 'PAI',
      principal: false,
    });
  };

  const confirmLinkExistingResp = () => {
    if (!existingRespFound) return;
    const { id, nome, cpf, email, telefone, parentesco, principal, dataNascimento } = existingRespFound;

    const newResp: ResponsavelItem = {
      id,
      nome,
      email,
      telefone: telefone ? formatPhone(telefone) : '',
      cpf: formatCPF(cpf),
      dataNascimento: dataNascimento ? new Date(dataNascimento).toISOString().slice(0, 10) : '',
      parentesco,
      principal: principal || responsaveis.filter(r => r.nome).length === 0,
    };

    setResponsaveis(prev => {
      const filled = prev.filter(r => r.nome);
      return [...filled, newResp];
    });

    toast.success(`${nome} vinculado com sucesso!`);
    setExistingRespFound(null);
    setShowNewRespForm(false);
    setNewRespForm({
      nome: '',
      cpf: '',
      dataNascimento: '',
      email: '',
      telefone: '',
      parentesco: 'PAI',
      principal: false,
    });
  };

  const handleSubmit = async () => {
    try {
      // Validar dados básicos do aluno
      if (!form.nome?.trim()) {
        toast.error('Nome do aluno é obrigatório.');
        setStep(0);
        return;
      }
      if (!form.dataNascimento) {
        toast.error('Data de nascimento do aluno é obrigatória.');
        setStep(0);
        return;
      }

      // Validar se há ao menos um responsável válido (Card 47)
      const hasResponsaveis = responsaveis.some((r) => r.nome?.trim());
      if (!hasResponsaveis) {
        toast.error('Informe ao menos um responsável com nome válido.');
        setStep(4);
        return;
      }

      // Validar CPF e data de nascimento de todos os responsáveis preenchidos
      for (let i = 0; i < responsaveis.length; i++) {
        const r = responsaveis[i];
        if (r.nome?.trim()) {
          const rCpf = stripMask(r.cpf);
          if (!rCpf || rCpf.length !== 11) {
            toast.error(`CPF é obrigatório e deve ter 11 dígitos para o responsável: ${r.nome}`);
            setStep(4);
            return;
          }
          if (!validateCPF(r.cpf)) {
            toast.error(`CPF inválido para o responsável: ${r.nome}`);
            setStep(4);
            return;
          }
          if (!r.dataNascimento) {
            toast.error(`Data de nascimento é obrigatória para o responsável: ${r.nome}`);
            setStep(4);
            return;
          }
        }
      }

      // Validar CPF se preenchido
      const cpfRaw = stripMask(form.cpf);
      if (cpfRaw && !validateCPF(cpfRaw)) {
        setCpfError('CPF inválido — verifique os dígitos');
        setStep(0);
        toast.error('CPF inválido. Corrija antes de salvar.');
        return;
      }

      // Primeiro responsável válido (para campos legados)
      const mainResp = responsaveis.find(r => r.nome) || null;

      const payload: any = {
        nome: form.nome,
        dataNascimento: form.dataNascimento,
        genero: form.genero,
        cpf: cpfRaw || undefined,
        matricula: form.matricula,
        turmaId: form.turmaId,
        // Responsável principal (legado + relacional)
        nomeResponsavel: mainResp?.nome || undefined,
        cpfResponsavel: mainResp ? stripMask(mainResp.cpf) || undefined : undefined,
        telefoneResponsavel: mainResp ? stripMask(mainResp.telefone) || undefined : undefined,
        emailResponsavel: mainResp?.email || undefined,
        dataNascimentoResponsavel: mainResp?.dataNascimento || undefined,
        parentesco: mainResp?.parentesco || undefined,
        responsaveis: responsaveis
          .filter((r) => r.nome)
          .map((r) => ({
            id: r.id,
            nome: r.nome,
            cpf: stripMask(r.cpf) || undefined,
            telefone: stripMask(r.telefone) || undefined,
            email: r.email || undefined,
            parentesco: r.parentesco || undefined,
            dataNascimento: r.dataNascimento || undefined,
            principal: r.principal,
          })),
        // RFID — etapa 4
        rfidCodigo: form.rfidCodigo || undefined,
        // Campos MEC (Sprint 2)
        corRaca: form.corRaca || undefined,
        nacionalidade: form.nacionalidade || undefined,
        paisOrigem: form.paisOrigem || undefined,
        nis: form.nis || undefined,
        // Endereço (Sprint 4)
        cep: form.cep || undefined,
        endereco: form.endereco || undefined,
        numero: form.numero || undefined,
        bairro: form.bairro || undefined,
        cidade: form.cidade || undefined,
        uf: form.uf || undefined,
        complemento: form.complemento || undefined,
        // Saúde (Sprint 4)
        alergias: form.alergias || undefined,
        tipoSanguineo: form.tipoSanguineo || undefined,
        restricaoAlimentar: form.restricaoAlimentar || undefined,
        planoSaude: form.planoSaude || undefined,
        tipoDeficiencia: form.tipoDeficiencia || undefined,
        deficienciaDetalhe: form.deficienciaDetalhe || undefined,
        // LGPD (Sprint 6)
        consentimentoLGPD: form.consentimentoLGPD === 'true',
      };

      if (editing) {
        await update.mutateAsync({ id: editing.id, ...payload });
        toast.success('Aluno atualizado!');
        onClose();
      } else {
        const result: any = await create.mutateAsync(payload);
        if (result?.id && fotoBase64Criar) {
          try {
            await api.post(UPLOAD.fotoAluno(result.id), { fotoBase64: fotoBase64Criar });
          } catch (err) {
            console.error('Erro ao enviar foto do novo aluno', err);
          }
        }
        // Limpar rascunho salvo
        if (draftId) removeDraft(draftId);
        // Diálogo de sucesso com confetes
        const turmaInfo = turmas.find(t => t.id === form.turmaId);
        setSuccessData({
          id: result?.id,
          nome: form.nome,
          matricula: result?.matricula || form.matricula || 'Auto',
          turma: turmaInfo?.nome || '',
          serie: turmaInfo?.serie || '',
          turno: turmaInfo?.turnoOperacional?.nome || turmaInfo?.turno || '',
          tokenAtivacao: result?.tokenAtivacao,
          codigoDisplay: result?.codigoDisplay,
        });
        // Confetes!
        setTimeout(() => {
          confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 }, colors: ['#16a34a', '#1B365D', '#f59e0b', '#8b5cf6'] });
        }, 200);
        // Notificar o pai para abrir a ficha do aluno recém-criado
        if (result?.id && onCreated) {
          setTimeout(() => onCreated(result.id), 300);
        }
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error?.message;
      if (Array.isArray(msg)) {
        toast.error(msg[0]);
      } else if (typeof msg === 'string') {
        toast.error(msg);
        if (msg.includes('CPF já está em uso')) {
          setCpfError(msg);
          setStep(0);
        }
      } else {
        toast.error('Erro ao salvar aluno');
      }
    }
  };

  const canAdvance = () => {
    if (step === 0) {
      if (cpfError) return false;
      const cpfRaw = stripMask(form.cpf);
      // CPF nunca bloqueia — apenas valida dígitos se preenchido
      const cpfValid = cpfRaw.length === 0 || (cpfRaw.length === 11 && validateCPF(cpfRaw));
      return form.nome.length >= 3 && form.dataNascimento && cpfValid;
    }
    if (step === 1) {
      if (!form.turmaId) return false;
      const sel = turmas.find(t => t.id === form.turmaId);
      if (sel?.capacidade && (sel._count?.alunos ?? 0) >= sel.capacidade) return false;
      return true;
    }
    if (step === 4) {
      return responsaveis.some((r) => r.nome?.trim());
    }
    return true;
  };

  /** Calcula idade a partir de data de nascimento ISO */
  const calcAge = (dateStr: string): number => {
    const birth = new Date(dateStr);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const handleWizardNext = async () => {
    if (step === 0 && !editing) {
      try {
        const cpfRaw = stripMask(form.cpf);
        const { data: check } = await api.post(ROUTES.alunos.listar(escolaId!) + '/check-duplicidade', {
          cpf: cpfRaw.length === 11 ? cpfRaw : undefined,
          nome: form.nome,
          dataNascimento: form.dataNascimento || undefined,
        });
        if (check.duplicate) { toast.error(check.message); return; }
        if (check.warning) { toast.warning(check.message); }
      } catch { /* se falhar, deixa prosseguir */ }
    }
    setStep((s) => s + 1);
    setTimeout(() => {
      modalBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  };

  const wizardPrimaryAction = step < STEPS.length - 1 ? (
    <button id="aluno-form-btn-proximo" type="button" className="btn-save" onClick={handleWizardNext} disabled={!canAdvance()}>
      Próximo →
    </button>
  ) : (
    <button
      id="aluno-form-btn-salvar"
      type="button"
      className="btn-save"
      onClick={handleSubmit}
      disabled={create.isPending || update.isPending || (!editing && form.consentimentoLGPD !== 'true')}
    >
      {(create.isPending || update.isPending) ? 'Salvando...' : editing ? 'Salvar Alterações' : 'Confirmar Matrícula'}
    </button>
  );

  const editModalFooter = (
    <div className="ac-aluno-edit-footer">
      <span className="ac-aluno-edit-footer-meta">
        Homologado: {new Date().toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
      </span>
      <div className="ac-aluno-edit-footer-actions">
        <button type="button" className="btn-cancel" onClick={onClose}>Cancelar</button>
        {step > 0 && (
          <button
            type="button"
            className="btn-cancel"
            onClick={() => {
              setStep((s) => s - 1);
              setTimeout(() => { modalBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }, 50);
            }}
          >
            ← Voltar
          </button>
        )}
        {wizardPrimaryAction}
      </div>
    </div>
  );

  const postWizardModals = (
    <>
      {avatarCropFile && (
        <AvatarCropper
          file={avatarCropFile}
          onCropped={onAvatarCropped}
          onCancel={() => setAvatarCropFile(null)}
        />
      )}
      {qrToken && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setQrToken(null); onClose(); }}>
          <div style={{ background: 'white', borderRadius: 16, padding: '32px 40px', maxWidth: 420, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,.2)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 6, color: '#1B365D' }}>✅ Aluno matriculado!</h3>
            <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 20 }}>
              Um link de ativação foi gerado para o responsável. Escaneie o QR Code ou copie o link abaixo.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <StyledQrCode
                value={`${window.location.origin}/ativar?token=${qrToken}${successData?.codigoDisplay ? `&code=${successData.codigoDisplay}` : ''}`}
                size={180}
                width={180}
                height={180}
                shape="square"
                style="rounded"
                icon="logo-shield-qrcode-backing"
                iconKind="preset"
                iconSize={80}
                hideBackgroundDots={false}
                foreground="#7c3aed"
                background="#ffffff"
                margin={0}
              />
            </div>
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', fontSize: '0.72rem', color: '#374151', wordBreak: 'break-all', marginBottom: 16, border: '1px solid #e5e7eb' }}>
              {window.location.origin}/ativar?token={qrToken}{successData?.codigoDisplay ? `&code=${successData.codigoDisplay}` : ''}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button className="btn-cancel" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/ativar?token=${qrToken}${successData?.codigoDisplay ? `&code=${successData.codigoDisplay}` : ''}`); toast.success('Link copiado!'); }}>
                Copiar Link
              </button>
              <button className="btn-save" onClick={() => { setQrToken(null); onClose(); }}>
                Concluir
              </button>
            </div>
          </div>
        </div>
      )}
      {successData && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, padding: '36px 40px', maxWidth: 480, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,.2)', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, #16a34a, #22c55e)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <PartyPopper size={28} color="white" />
            </div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#14532d', marginBottom: 4 }}>Matricula Realizada!</h3>
            <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 20 }}>O aluno foi cadastrado com sucesso no sistema.</p>
            <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '16px 20px', border: '1px solid #86efac', textAlign: 'left', marginBottom: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: '0.82rem' }}>
                <div><strong style={{ color: '#6b7280' }}>Aluno:</strong> {successData.nome}</div>
                <div><strong style={{ color: '#6b7280' }}>Matricula:</strong> {successData.matricula}</div>
                <div><strong style={{ color: '#6b7280' }}>Turma:</strong> {successData.turma}</div>
                <div><strong style={{ color: '#6b7280' }}>Serie:</strong> {successData.serie}</div>
                <div><strong style={{ color: '#6b7280' }}>Turno:</strong> {successData.turno}</div>
              </div>
            </div>
            <div className="ac-aluno-wizard-step">
              <button onClick={async () => {
                try {
                  const res = await api.get(ROUTES.alunos.findOne(escolaId!, successData.id) + '/pdf/termo-lgpd', { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                  const a = document.createElement('a'); a.href = url; a.download = `termo-lgpd-${successData.nome}.pdf`; a.click();
                  window.URL.revokeObjectURL(url);
                } catch { toast.error('Erro ao gerar PDF'); }
              }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 20px', background: '#1B365D', color: 'white', border: 'none', borderRadius: 8, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', width: '100%' }}>
                <Download size={16} />Baixar Termo de Consentimento LGPD
              </button>
              {successData.tokenAtivacao && (
                <button onClick={() => { setQrToken(successData.tokenAtivacao); setSuccessData(null); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px 20px', background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', width: '100%' }}>
                  <UserCheck size={15} />Ver QR Code de Ativacao do Responsavel
                </button>
              )}
              <button onClick={() => { setSuccessData(null); onClose(); }} style={{ padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', width: '100%' }}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const editPhotoBlock = (
    <div className="ac-modal-dual__photo ac-responsavel-modal-photo">
      <button
        type="button"
        className={`ac-responsavel-avatar-btn ${fotoUploading ? 'ac-responsavel-avatar-btn--busy' : ''}`}
        disabled={fotoUploading || create.isPending || update.isPending}
        onClick={() => { if (!fotoUploading) avatarFileInputRef.current?.click(); }}
        title="Alterar foto"
      >
        {hasFotoReal(modalFotoSrc) ? (
          <img src={modalFotoSrc!} alt="" />
        ) : (
          <span className="ac-responsavel-avatar-placeholder">
            <User size={22} strokeWidth={1.5} />
          </span>
        )}
        <span className="ac-responsavel-avatar-cam" aria-hidden>
          <Camera size={11} strokeWidth={2} />
        </span>
      </button>
      <input
        ref={avatarFileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="ac-responsavel-avatar-file-input"
        onChange={onAvatarFilePicked}
        tabIndex={-1}
        aria-hidden
      />
      <div className="ac-responsavel-modal-photo-hint">
        <strong>Foto do aluno</strong>
        <span>Toque na foto para escolher uma imagem (JPEG, PNG ou WebP, até 5 MB).</span>
      </div>
    </div>
  );

  const matriculaDraftsUI = showDrafts ? (() => {
          const drafts = getDrafts();
          return (
            <div className="modal-body" style={{ minHeight: 200 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <FileText size={18} style={{ color: '#d97706' }} />
                <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1B365D' }}>Matrículas em andamento</span>
              </div>
              <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 12 }}>
                Você tem {drafts.length} rascunho(s) salvos. Retome ou inicie uma nova matrícula.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                {drafts.map(d => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                    <Clock size={14} style={{ color: '#d97706', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.nome || 'Sem nome'}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>
                        Etapa {(d.step || 0) + 1}/6 — {new Date(d.updatedAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    <button type="button" onClick={() => resumeDraft(d)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 12px', background: '#1B365D', color: 'white', border: 'none', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <RefreshCw size={11} />Retomar
                    </button>
                    <button type="button" onClick={() => { removeDraft(d.id); if (getDrafts().length === 0) startNewDraft(); else setShowDrafts(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 4 }} title="Remover rascunho">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={startNewDraft} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: '10px', background: '#1B365D', border: 'none', borderRadius: 8, cursor: 'pointer', color: 'white', fontSize: '0.82rem', fontWeight: 600 }}>
                <Plus size={14} /> Iniciar nova matrícula
              </button>
            </div>
          );
  })() : null;

  const wizardUI = !showDrafts ? (
    <>
        <div className="ac-aluno-stepper">
          {STEPS.map((label, i) => (
            <div
              key={i}
              className="ac-aluno-stepper__item"
              onClick={() => {
                if (i < step) {
                  setStep(i);
                }
              }}
              style={{ cursor: i < step ? 'pointer' : 'default' }}
            >
              <div className="ac-aluno-stepper__node">
                <span
                  className="ac-aluno-stepper__badge"
                  style={{
                    background: i < step ? '#16a34a' : i === step ? '#1B365D' : '#f1f5f9',
                    color: i < step ? '#ffffff' : i === step ? '#ffffff' : '#64748b',
                    border: i === step ? '2px solid #1B365D' : i < step ? '2px solid #16a34a' : '1px solid #cbd5e1',
                    boxShadow: i === step ? '0 0 0 3px rgba(27, 54, 93, 0.15)' : 'none',
                  }}
                >{i < step ? '✓' : i + 1}</span>
                <span
                  className="ac-aluno-stepper__label"
                  style={{
                    color: i === step ? '#1B365D' : i < step ? '#16a34a' : '#64748b',
                    fontWeight: i === step ? 700 : 600,
                  }}
                >{label}</span>
              </div>
            </div>
          ))}
        </div>

        <div ref={modalBodyRef} className="modal-body wizard-compact">
          {/* Step 0: Dados Pessoais */}
          {step === 0 && (
            <div className="ac-aluno-step0-wrap">
              {/* Foto do Aluno */}
              <div className="ac-aluno-avatar-box">
                <button
                  type="button"
                  className={`ac-responsavel-avatar-btn ${fotoUploading ? 'ac-responsavel-avatar-btn--busy' : ''}`}
                  disabled={fotoUploading || create.isPending || update.isPending}
                  onClick={() => { if (!fotoUploading) avatarFileInputRef.current?.click(); }}
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: '50%',
                    border: '2px dashed #cbd5e1',
                    background: '#f8fafc',
                    position: 'relative',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title={fotoUploading ? 'Enviando foto...' : (modalFotoSrc ? 'Clique para alterar a foto' : 'Clique para adicionar foto')}
                >
                  {hasFotoReal(modalFotoSrc) ? (
                    <img src={modalFotoSrc!} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: '1.8rem', fontWeight: 700, color: '#64748b' }}>
                      {form.nome ? form.nome.charAt(0).toUpperCase() : '?'}
                    </span>
                  )}
                  {!fotoUploading && (
                    <span className="ac-responsavel-avatar-cam" style={{ position: 'absolute', bottom: 4, right: 4 }}>
                      <Camera size={10} />
                    </span>
                  )}
                </button>
                <input
                  ref={avatarFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={onAvatarFilePicked}
                />
                <span style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 600 }}>Foto (Opcional)</span>
              </div>

              {/* Grid de campos */}
              <div className="ac-aluno-step0-grid">
                <div className="form-group ac-aluno-field-full">
                  <label className="form-label">Nome Completo *</label>
                  <input id="aluno-form-nome" className="form-input" value={form.nome} onChange={e => set('nome', e.target.value)} placeholder="Nome do aluno" />
                </div>
                <div className="form-group ac-aluno-field-half">
                  <label className="form-label">Data de Nascimento *</label>
                  <input id="aluno-form-dt-nasc" className="form-input" type="date" value={form.dataNascimento} onChange={e => set('dataNascimento', e.target.value)} />
                </div>
                <div className="form-group ac-aluno-field-half">
                  <label className="form-label">Gênero *</label>
                  <select id="aluno-form-genero" className="form-select" value={form.genero} onChange={e => set('genero', e.target.value)}>
                    <option value="MASCULINO">Masculino</option>
                    <option value="FEMININO">Feminino</option>
                    <option value="OUTRO">Outro</option>
                  </select>
                </div>
                <div className="form-group ac-aluno-field-half">
                  <label className="form-label">CPF {form.dataNascimento && calcAge(form.dataNascimento) >= 16 ? '(recomendado)' : '(opcional)'}</label>
                  <input
                    id="aluno-form-cpf"
                    className="form-input"
                    value={form.cpf}
                    onChange={e => handleCPFChange(e.target.value)}
                    onBlur={handleCPFBlur}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    style={cpfError ? { borderColor: '#dc2626' } : undefined}
                  />
                  {cpfError && (
                    <span style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: 4, display: 'block' }}>{cpfError}</span>
                  )}
                </div>
                <div className="form-group ac-aluno-field-half">
                  <label className="form-label">NIS (opcional)</label>
                  <input id="aluno-form-nis" className="form-input" value={form.nis} onChange={e => set('nis', e.target.value.replace(/\D/g, '').slice(0, 11))} placeholder="00000000000" maxLength={11} />
                </div>
                <div className="form-group ac-aluno-field-half">
                  <label className="form-label">Cor/Raça</label>
                  <select id="aluno-form-cor-raca" className="form-select" value={form.corRaca} onChange={e => set('corRaca', e.target.value)}>
                    <option value="">Não declarada</option>
                    <option value="BRANCA">Branca</option>
                    <option value="PRETA">Preta</option>
                    <option value="PARDA">Parda</option>
                    <option value="AMARELA">Amarela</option>
                    <option value="INDIGENA">Indígena</option>
                  </select>
                </div>
                <div className="form-group ac-aluno-field-half">
                  <label className="form-label">Nacionalidade</label>
                  <select id="aluno-form-nacionalidade" className="form-select" value={form.nacionalidade} onChange={e => set('nacionalidade', e.target.value)}>
                    <option value="BRASILEIRO_NATO">Brasileiro nato</option>
                    <option value="NATURALIZADO">Naturalizado</option>
                    <option value="ESTRANGEIRO">Estrangeiro</option>
                  </select>
                </div>
                {form.nacionalidade === 'ESTRANGEIRO' && (
                  <div className="form-group ac-aluno-field-full">
                    <label className="form-label">País de Origem</label>
                    <input id="aluno-form-pais-origem" className="form-input" value={form.paisOrigem} onChange={e => set('paisOrigem', e.target.value)} placeholder="Ex: Argentina, Haiti, Venezuela" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 1: Acadêmico (cascata Turno → Série → Turma) */}
          {step === 1 && (() => {
            const turmasAtivas = turmas.filter(t => t.ativo);
            const getTurno = (t: Turma) => t.turnoOperacional?.nome || t.turno || '';
            const turnosDeTurmas = turmasAtivas.map(getTurno).filter(Boolean);
            const turnosDeEscola = (turnos || []).map((t: any) => t.nome).filter(Boolean);
            const turnosUnicos = [...new Set([...turnosDeTurmas, ...turnosDeEscola])].sort();

            const seriesDeTurmas = turmasAtivas
              .filter(t => !filtroTurno || getTurno(t) === filtroTurno)
              .map(t => t.serie)
              .filter(Boolean);

            const seriesDeEscola = (turnos || [])
              .filter((t: any) => !filtroTurno || t.nome === filtroTurno)
              .flatMap((t: any) => {
                const arr = Array.isArray(t.seriesAtendidas) ? t.seriesAtendidas : [];
                return arr.map((s: string) => SERIE_LABELS[s] || s);
              })
              .filter(Boolean);

            const seriesFiltradas = [...new Set([...seriesDeTurmas, ...seriesDeEscola])].sort();

            const turmasFiltradas = turmasAtivas.filter(t => {
              if (filtroTurno && getTurno(t) !== filtroTurno) return false;
              if (filtroSerie && t.serie !== filtroSerie) return false;
              return true;
            });

            const turmaSel = form.turmaId ? turmasAtivas.find(x => x.id === form.turmaId) : null;
            const countSel = turmaSel?._count?.alunos ?? 0;
            const capSel = turmaSel?.capacidade ?? 0;
            const vagasSel = capSel > 0 ? capSel - countSel : -1;
            const lotada = capSel > 0 && countSel >= capSel;

            return (
            <div className="ac-aluno-wizard-step ac-aluno-wizard-step--academico">
              <div className="ac-aluno-step-grid2">
                <div className="form-group ac-aluno-field-full ac-aluno-field--matricula">
                  <label className="form-label">Matrícula</label>
                  <div className="ac-aluno-matricula-row">
                    <input id="aluno-form-matricula" className="form-input" value={form.matricula} onChange={e => set('matricula', e.target.value)} placeholder="Deixe vazio para gerar automaticamente" />
                    <span className={`ac-aluno-matricula-badge${form.matricula ? '' : ' ac-aluno-matricula-badge--auto'}`}>
                      {form.matricula ? 'Manual' : '✓ Auto'}
                    </span>
                  </div>
                </div>
                <div className="form-group ac-aluno-field-half">
                  <label className="form-label">Turno</label>
                  <select id="aluno-form-filtro-turno" className="form-select" value={filtroTurno} onChange={e => { setFiltroTurno(e.target.value); setFiltroSerie(''); set('turmaId', ''); }}>
                    <option value="">Todos</option>
                    {turnosUnicos.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group ac-aluno-field-half">
                  <label className="form-label">Série / Ano</label>
                  <select id="aluno-form-filtro-serie" className="form-select" value={filtroSerie} onChange={e => { setFiltroSerie(e.target.value); set('turmaId', ''); }}>
                    <option value="">Todas</option>
                    {seriesFiltradas.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group ac-aluno-field-full">
                  <label className="form-label">Turma *</label>
                  <select id="aluno-form-turma" className="form-select" value={form.turmaId} onChange={e => set('turmaId', e.target.value)} style={{ fontWeight: form.turmaId ? 600 : 400 }}>
                    <option value="">Selecione...</option>
                    {turmasFiltradas.map(t => (
                      <option key={t.id} value={t.id}>{t.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Info sutil da turma selecionada */}
              {turmaSel && (() => {
                const pctUsado = capSel > 0 ? Math.min((countSel / capSel) * 100, 100) : 0;
                const barColor = lotada ? '#dc2626' : pctUsado > 80 ? '#f59e0b' : '#16a34a';
                return (
                  <div className="ac-aluno-turma-info" style={{ marginTop: 2 }}>
                    <p style={{ fontSize: '0.78rem', color: lotada ? '#dc2626' : '#6b7280', margin: '0 0 6px 0' }}>
                      {turmaSel.nome} · {getTurno(turmaSel) || '—'}{turmaSel.sala ? ` · Sala ${turmaSel.sala}` : ''}
                      {' — '}
                      <span style={{ fontWeight: 600, color: lotada ? '#dc2626' : '#374151' }}>
                        {lotada ? 'Turma lotada' : `${vagasSel} vaga${vagasSel !== 1 ? 's' : ''} disponíve${vagasSel !== 1 ? 'is' : 'l'}`}
                      </span>
                    </p>
                    {capSel > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          flex: 1, height: 6, borderRadius: 3,
                          background: '#e5e7eb', overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${pctUsado}%`, height: '100%', borderRadius: 3,
                            background: barColor,
                            transition: 'width 0.4s ease',
                          }} />
                        </div>
                        <span style={{ fontSize: '0.68rem', color: '#9ca3af', whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {countSel}/{capSel}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );})()}

          {/* Step 2: Endereço + mapa */}
          {step === 2 && (
            <div className="ac-aluno-wizard-step ac-aluno-wizard-step--endereco">
              <p className="ac-aluno-wizard-hint">
                Opcional. Informe o CEP para preenchimento automático e confira a localização no mapa.
              </p>
              <div className="ac-aluno-wizard-endereco">
                <div className="ac-aluno-step-grid2">
                  <div className="form-group ac-aluno-field-half">
                    <label className="form-label">CEP</label>
                    <input id="aluno-form-cep" className="form-input" value={formatCEP(form.cep)} onChange={e => {
                      const v = stripMask(e.target.value).slice(0, 8);
                      set('cep', v);
                      if (v.length === 8) {
                        fetch(`https://viacep.com.br/ws/${v}/json/`).then(r => r.json()).then(d => {
                          if (!d.erro) {
                            setForm(prev => ({ ...prev, endereco: d.logradouro || '', bairro: d.bairro || '', cidade: d.localidade || '', uf: d.uf || '' }));
                          }
                        }).catch(() => {});
                      }
                    }} placeholder="00000-000" maxLength={9} />
                  </div>
                  <div className="form-group ac-aluno-field-half">
                    <label className="form-label">Bairro</label>
                    <input id="aluno-form-bairro" className="form-input" value={form.bairro} onChange={e => set('bairro', e.target.value)} placeholder="Bairro" />
                  </div>
                  <div className="form-group ac-aluno-field-full">
                    <label className="form-label">Endereço</label>
                    <input id="aluno-form-endereco" className="form-input" value={form.endereco} onChange={e => set('endereco', e.target.value)} placeholder="Rua, Avenida..." />
                  </div>
                  <div className="form-group ac-aluno-field-half">
                    <label className="form-label">Número</label>
                    <input id="aluno-form-numero" className="form-input" value={form.numero} onChange={e => set('numero', e.target.value)} placeholder="Nº da casa / apto" />
                  </div>
                  <div className="form-group ac-aluno-field-half">
                    <label className="form-label">Complemento</label>
                    <input id="aluno-form-complemento" className="form-input" value={form.complemento} onChange={e => set('complemento', e.target.value)} placeholder="Apto, Bloco, Casa..." />
                  </div>
                  <div className="form-group ac-aluno-field-half">
                    <label className="form-label">Cidade</label>
                    <input id="aluno-form-cidade" className="form-input" value={form.cidade} onChange={e => set('cidade', e.target.value)} placeholder="Cidade" />
                  </div>
                  <div className="form-group ac-aluno-field-half">
                    <label className="form-label">UF</label>
                    <input id="aluno-form-uf" className="form-input" value={form.uf} onChange={e => set('uf', e.target.value.toUpperCase().slice(0, 2))} placeholder="SP" maxLength={2} />
                  </div>
                </div>
                <AlunoEnderecoMap
                  cep={form.cep}
                  endereco={form.endereco}
                  numero={form.numero}
                  bairro={form.bairro}
                  cidade={form.cidade}
                  uf={form.uf}
                  latitude={alunoDetalhe?.latitude ?? editing?.latitude}
                  longitude={alunoDetalhe?.longitude ?? editing?.longitude}
                />
              </div>
            </div>
          )}

          {/* Step 3: Saúde */}
          {step === 3 && (
            <div className="ac-aluno-wizard-step ac-aluno-wizard-step--saude">
              <p className="ac-aluno-wizard-hint">
                Opcional. Informações de saúde importantes para a escola.
              </p>
              <div className="ac-aluno-step-grid2">
                <div className="form-group ac-aluno-field-half">
                  <label className="form-label">Tipo Sanguíneo</label>
                  <select id="aluno-form-tipo-sang" className="form-select" value={form.tipoSanguineo} onChange={e => set('tipoSanguineo', e.target.value)}>
                    <option value="">Não informado</option>
                    <option value="A+">A+</option><option value="A-">A-</option>
                    <option value="B+">B+</option><option value="B-">B-</option>
                    <option value="AB+">AB+</option><option value="AB-">AB-</option>
                    <option value="O+">O+</option><option value="O-">O-</option>
                  </select>
                </div>
                <div className="form-group ac-aluno-field-half">
                  <label className="form-label">Plano de Saúde</label>
                  <input id="aluno-form-plano-saude" className="form-input" value={form.planoSaude} onChange={e => set('planoSaude', e.target.value)} placeholder="Nome do plano ou SUS" />
                </div>
                <div className="form-group ac-aluno-field-full">
                  <label className="form-label">Alergias</label>
                  <input id="aluno-form-alergias" className="form-input" value={form.alergias} onChange={e => set('alergias', e.target.value)} placeholder="Ex: Lactose, Amendoim..." />
                </div>
                <div className="form-group ac-aluno-field-full">
                  <label className="form-label">Restrição Alimentar</label>
                  <input id="aluno-form-restricao-alim" className="form-input" value={form.restricaoAlimentar} onChange={e => set('restricaoAlimentar', e.target.value)} placeholder="Ex: Vegetariano, Sem glúten..." />
                </div>
                <div className="form-group ac-aluno-field-half">
                  <label className="form-label">Deficiência / NEE</label>
                  <select id="aluno-form-deficiencia" className="form-select" value={form.tipoDeficiencia} onChange={e => set('tipoDeficiencia', e.target.value)}>
                    <option value="">Nenhuma</option>
                    <option value="VISUAL">Visual</option>
                    <option value="AUDITIVA">Auditiva</option>
                    <option value="FISICA">Física</option>
                    <option value="INTELECTUAL">Intelectual</option>
                    <option value="MULTIPLA">Múltipla</option>
                    <option value="AUTISMO">Autismo / TEA</option>
                    <option value="ALTAS_HABILIDADES">Altas Habilidades</option>
                    <option value="OUTRA">Outra</option>
                  </select>
                </div>
                {form.tipoDeficiencia ? (
                  <div className="form-group ac-aluno-field-half">
                    <label className="form-label">Detalhes da deficiência / NEE</label>
                    <input id="aluno-form-deficiencia-detalhe" className="form-input" value={form.deficienciaDetalhe} onChange={e => set('deficienciaDetalhe', e.target.value)} placeholder="Descreva brevemente..." />
                  </div>
                ) : <div className="ac-aluno-field-half" />}
              </div>
            </div>
          )}

          {/* Step 4: Responsável — somente busca (cadastro no módulo Responsáveis) */}
          {step === 4 && (
            <div className="ac-aluno-wizard-step ac-aluno-wizard-step--responsavel">
              <div className="ac-aluno-wizard-stack">
              {editing && loadingAlunoDetalhe && (
                <p style={{ fontSize: '0.75rem', color: '#2563eb', margin: 0 }}>A carregar responsáveis já vinculados…</p>
              )}
              {/* Busca de responsável existente */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', position: 'relative' }}>
                <div ref={searchBoxRef} className="form-group ac-resp-search-box" style={{ position: 'relative', flex: 1, minWidth: 0, marginBottom: 0 }}>
                  <label className="form-label">
                    <Search size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Buscar responsável cadastrado
                  </label>
                  <input
                    id="aluno-form-resp-busca"
                    className="form-input"
                    value={searchQuery}
                    onFocus={handleSearchFocus}
                    onClick={handleSearchFocus}
                    onChange={e => handleSearch(e.target.value)}
                    placeholder="Clique para ver a lista ou digite nome, CPF..."
                    autoComplete="off"
                  />
                  {searching && (
                    <span style={{ position: 'absolute', right: 12, top: 31, fontSize: '0.72rem', color: '#6366f1', fontWeight: 600 }}>
                      Buscando...
                    </span>
                  )}

                  {/* DROPDOWN PERSONALIZADO EM TEMPO REAL */}
                  {searchDropdownOpen && (
                    <div
                      className="ac-resp-search-dropdown"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: '#ffffff',
                        border: '1.5px solid #6366f1',
                        borderRadius: 10,
                        boxShadow: '0 14px 35px rgba(0, 0, 0, 0.22)',
                        zIndex: 500,
                        maxHeight: 280,
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                      }}
                    >
                      {/* Header do Dropdown */}
                      <div
                        style={{
                          padding: '8px 12px',
                          background: '#f8fafc',
                          borderBottom: '1px solid #e2e8f0',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          color: '#475569',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 6,
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                          <span>👥</span>
                          <span>{searching ? 'Pesquisando...' : `${displayedSearchResults.length} disponível(is)`}</span>
                        </span>
                        <span style={{ fontSize: '0.68rem', color: '#6366f1', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          Clique para vincular
                        </span>
                      </div>

                      {/* Lista de Responsáveis */}
                      {displayedSearchResults.length > 0 ? (
                        displayedSearchResults.map((r: any) => {
                          const isAlreadySelected = responsaveis.some(
                            item => item.id === r.id || (r.cpf && stripMask(item.cpf) === stripMask(r.cpf))
                          );
                          const filhosCount = r.alunos?.length || 0;
                          const filhosNomes = (r.alunos || [])
                            .map((a: any) => a.aluno?.nome)
                            .filter(Boolean)
                            .slice(0, 2)
                            .join(', ');

                          return (
                            <div
                              key={r.id}
                              onClick={() => selectFromSearch(r)}
                              style={{
                                width: '100%',
                                padding: '9px 12px',
                                borderBottom: '1px solid #f1f5f9',
                                background: isAlreadySelected ? '#f0fdf4' : '#ffffff',
                                textAlign: 'left',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                                transition: 'background 0.15s ease',
                              }}
                              onMouseEnter={e => {
                                if (!isAlreadySelected) e.currentTarget.style.background = '#f8fafc';
                              }}
                              onMouseLeave={e => {
                                if (!isAlreadySelected) e.currentTarget.style.background = '#ffffff';
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                                <ListAvatar fotoUrl={r.fotoUrl} nome={r.nome} size={32} />
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>
                                      {r.nome}
                                    </span>
                                    {r.parentesco && (
                                      <span
                                        style={{
                                          fontSize: '0.66rem',
                                          background: '#e0e7ff',
                                          color: '#4338ca',
                                          padding: '1px 5px',
                                          borderRadius: 4,
                                          fontWeight: 700,
                                        }}
                                      >
                                        {r.parentesco}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {r.cpf ? `CPF: ${formatCPF(r.cpf)}` : ''}
                                    {r.telefone ? ` • Tel: ${formatPhone(String(r.telefone))}` : ''}
                                    {r.email ? ` • ${r.email}` : ''}
                                  </div>
                                  {filhosCount > 0 && (
                                    <div style={{ fontSize: '0.66rem', color: '#059669', marginTop: 1, fontWeight: 600 }}>
                                      👶 Já vinculado a: {filhosNomes} {filhosCount > 2 ? `(+${filhosCount - 2})` : ''}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div style={{ flexShrink: 0 }}>
                                {isAlreadySelected ? (
                                  <span
                                    style={{
                                      fontSize: '0.7rem',
                                      fontWeight: 800,
                                      color: '#16a34a',
                                      background: '#dcfce7',
                                      padding: '3px 8px',
                                      borderRadius: 6,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 3,
                                    }}
                                  >
                                    ✓ Vinculado
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      fontSize: '0.72rem',
                                      fontWeight: 700,
                                      color: '#2563eb',
                                      background: '#eff6ff',
                                      padding: '3px 8px',
                                      borderRadius: 6,
                                      border: '1px solid #bfdbfe',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 3,
                                    }}
                                  >
                                    + Vincular
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ padding: '18px 14px', textAlign: 'center', color: '#64748b' }}>
                          <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#334155' }}>
                            {searchQuery ? `Nenhum responsável encontrado para "${searchQuery}"` : 'Nenhum responsável cadastrado nesta escola.'}
                          </div>
                          <div style={{ fontSize: '0.74rem', color: '#94a3b8', marginTop: 4 }}>
                            Clique no botão ao lado "+ Novo Responsável" para cadastrar e vincular agora.
                          </div>
                          {searchQuery && (
                            <button
                              type="button"
                              onClick={() => {
                                setSearchDropdownOpen(false);
                                setNewRespForm(prev => ({ ...prev, nome: searchQuery }));
                                setShowNewRespForm(true);
                              }}
                              style={{
                                marginTop: 12,
                                fontSize: '0.76rem',
                                padding: '6px 14px',
                                background: '#6366f1',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontWeight: 700,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                              }}
                            >
                              <Plus size={13} /> Cadastrar "{searchQuery}" como Novo Responsável
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowNewRespForm(!showNewRespForm)}
                  className={`btn ${showNewRespForm ? 'btn-secondary' : 'btn-primary'} ac-btn-novo-resp`}
                  style={{ height: 38, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
                  title={showNewRespForm ? 'Cancelar cadastro rápido' : 'Cadastrar Novo Responsável'}
                  aria-label={showNewRespForm ? 'Cancelar cadastro rápido' : 'Cadastrar Novo Responsável'}
                >
                  {showNewRespForm ? <X size={15} /> : <Plus size={15} />}
                  <span className="ac-btn-novo-resp-label">{showNewRespForm ? 'Cancelar' : 'Novo Responsável'}</span>
                </button>
              </div>

              {/* Formulário de novo responsável embutido */}
              {showNewRespForm && (
                <div style={{ padding: 14, background: 'var(--bg-muted, #f8fafc)', border: '1px dashed var(--border-color, #cbd5e1)', borderRadius: 8, marginTop: 10 }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-color, #1e293b)' }}>Cadastrar Novo Responsável</h4>
                  <div className="ac-aluno-step-grid2">
                    <div className="form-group">
                      <label className="form-label">Nome Completo *</label>
                      <input 
                        className={`form-input${newRespErrors.nome ? ' form-input--error' : ''}`}
                        value={newRespForm.nome} 
                        onChange={e => {
                          setNewRespForm({ ...newRespForm, nome: e.target.value });
                          if (newRespErrors.nome) setNewRespErrors(prev => { const n = { ...prev }; delete n.nome; return n; });
                        }} 
                        placeholder="Nome do responsável" 
                      />
                      {newRespErrors.nome && <span className="form-error-text" style={{ fontSize: '0.7rem', color: '#ef4444', marginTop: 2, display: 'block' }}>{newRespErrors.nome}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">CPF *</label>
                      <input 
                        className={`form-input${newRespErrors.cpf ? ' form-input--error' : ''}`}
                        value={newRespForm.cpf} 
                        onChange={e => {
                          setNewRespForm({ ...newRespForm, cpf: formatCPF(e.target.value) });
                          if (newRespErrors.cpf) setNewRespErrors(prev => { const n = { ...prev }; delete n.cpf; return n; });
                        }} 
                        onBlur={() => checkNewRespCpfLive(newRespForm.cpf)}
                        placeholder="000.000.000-00" 
                        maxLength={14}
                      />
                      {newRespErrors.cpf && <span className="form-error-text" style={{ fontSize: '0.7rem', color: '#ef4444', marginTop: 2, display: 'block' }}>{newRespErrors.cpf}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Data de Nascimento *</label>
                      <input 
                        className={`form-input${newRespErrors.dataNascimento ? ' form-input--error' : ''}`}
                        type="date" 
                        value={newRespForm.dataNascimento} 
                        onChange={e => {
                          setNewRespForm({ ...newRespForm, dataNascimento: e.target.value });
                          if (newRespErrors.dataNascimento) setNewRespErrors(prev => { const n = { ...prev }; delete n.dataNascimento; return n; });
                        }} 
                      />
                      {newRespErrors.dataNascimento && <span className="form-error-text" style={{ fontSize: '0.7rem', color: '#ef4444', marginTop: 2, display: 'block' }}>{newRespErrors.dataNascimento}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Grau de Parentesco *</label>
                      <select className="form-select" value={newRespForm.parentesco} onChange={e => setNewRespForm({ ...newRespForm, parentesco: e.target.value })}>
                        <option value="PAI">Pai</option>
                        <option value="MAE">Mãe</option>
                        <option value="OUTRO">Outro / Tutor</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">E-mail (Opcional)</label>
                      <input 
                        className={`form-input${newRespErrors.email ? ' form-input--error' : ''}`}
                        type="email"
                        value={newRespForm.email} 
                        onChange={e => {
                          setNewRespForm({ ...newRespForm, email: e.target.value });
                          if (newRespErrors.email) setNewRespErrors(prev => { const n = { ...prev }; delete n.email; return n; });
                        }} 
                        onBlur={() => checkNewRespEmailLive(newRespForm.email)}
                        placeholder="email@exemplo.com" 
                      />
                      {newRespErrors.email && <span className="form-error-text" style={{ fontSize: '0.7rem', color: '#ef4444', marginTop: 2, display: 'block' }}>{newRespErrors.email}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Telefone (Opcional)</label>
                      <input 
                        className="form-input" 
                        value={newRespForm.telefone} 
                        onChange={e => setNewRespForm({ ...newRespForm, telefone: formatPhone(e.target.value) })} 
                        placeholder="(00) 00000-0000" 
                      />
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={newRespForm.principal} onChange={e => setNewRespForm({ ...newRespForm, principal: e.target.checked })} />
                      Definir como Responsável Principal
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                    <button type="button" onClick={() => setShowNewRespForm(false)} className="btn btn-text" style={{ padding: '6px 12px', fontSize: '0.8rem' }}>Cancelar</button>
                    <button type="button" onClick={handleLinkNewResponsavel} className="btn btn-primary" style={{ padding: '6px 16px', fontSize: '0.8rem' }} disabled={checkingNewResp}>
                      {checkingNewResp ? 'Validando dados...' : 'Vincular Novo'}
                    </button>
                  </div>
                </div>
              )}

              {/* Popup de confirmação se CPF existe */}
              {existingRespFound && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                  <div style={{ background: 'white', padding: 24, borderRadius: 12, maxWidth: 400, width: '90%', boxShadow: '0 10px 25px rgba(0,0,0,0.15)' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>Responsável já cadastrado</h3>
                    <p style={{ fontSize: '0.82rem', color: '#475569', margin: '0 0 20px 0', lineHeight: 1.5 }}>
                      O CPF digitado pertence ao responsável <strong>{existingRespFound.nome}</strong>. Deseja vinculá-lo diretamente a este aluno?
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                      <button type="button" onClick={() => setExistingRespFound(null)} className="btn btn-secondary" style={{ padding: '8px 16px', fontSize: '0.8rem' }}>Não, cancelar</button>
                      <button type="button" onClick={confirmLinkExistingResp} className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '0.8rem' }}>Sim, vincular ele</button>
                    </div>
                  </div>
                </div>
              )}
              {responsaveis.some((r) => r.nome) && (
                <p className="ac-aluno-resp-summary-label">
                  {responsaveis.filter((r) => r.nome).length} vinculado(s)
                </p>
              )}
              <ul className="ac-aluno-resp-summary-list">
                {responsaveis.filter((r) => r.nome).map((resp, idx) => (
                  <li key={resp.id || `resp-summary-${idx}`} className="ac-aluno-resp-summary-item">
                    <ListAvatar fotoUrl={resp.fotoUrl} nome={resp.nome} size={36} />
                    <div className="ac-aluno-resp-summary-main">
                      <div className="ac-aluno-resp-summary-top">
                        <span className="ac-aluno-resp-summary-name" title={resp.nome}>{resp.nome}</span>
                        {resp.principal && (
                          <span className="ac-aluno-resp-summary-pill">Principal</span>
                        )}
                      </div>
                      <span className="ac-aluno-resp-summary-meta" title={respResumoMeta(resp)}>
                        {respResumoMeta(resp)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="ac-aluno-resp-summary-remove"
                      onClick={() => removeResp(idx)}
                      title="Desvincular"
                      aria-label={`Desvincular ${resp.nome}`}
                    >
                      <Trash2 size={14} strokeWidth={1.75} />
                    </button>
                  </li>
                ))}
              </ul>
              {responsaveis.filter((r) => r.nome).length === 0 && (
                <div className="ac-aluno-resp-empty">
                  <ContactRound size={22} strokeWidth={1.5} className="ac-aluno-resp-empty__icon" />
                  <p className="ac-aluno-resp-empty__title">Nenhum responsável vinculado</p>
                  <p className="ac-aluno-resp-empty__text">
                    Use a busca acima para vincular um cadastro existente ou registre o responsável em <strong>Responsáveis</strong>.
                  </p>
                </div>
              )}
              </div>
            </div>
          )}

          {/* Step 5: RFID */}
          {step === 5 && (
            <div className="ac-aluno-wizard-step ac-aluno-wizard-step--rfid">
              <p className="ac-aluno-wizard-hint">
                Opcional. Vincule um cartão ou pulseira RFID ao aluno para controle de acesso e revise o resumo.
              </p>
              <div className="ac-aluno-step5-grid">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div className="form-group">
                    <label className="form-label">Código RFID</label>
                    <input id="aluno-form-rfid" className="form-input" value={form.rfidCodigo} onChange={e => set('rfidCodigo', e.target.value)} placeholder="Aproxime o cartão ou digite o código" />
                  </div>
                  
                  {/* LGPD Consent */}
                  {!editing && (
                    <div style={{ background: '#eff6ff', borderRadius: 8, padding: 12, border: '1px solid #bfdbfe' }}>
                      <label style={{ display: 'flex', gap: 8, cursor: 'pointer', alignItems: 'flex-start', fontSize: '0.74rem', color: '#1e40af', lineHeight: 1.3 }}>
                        <input id="aluno-form-lgpd" type="checkbox" checked={form.consentimentoLGPD === 'true'} onChange={e => set('consentimentoLGPD', e.target.checked ? 'true' : 'false')} style={{ marginTop: 2, accentColor: '#1B365D', width: 14, height: 14, flexShrink: 0 }} />
                        <span>Declaro que obtive o <strong>consentimento do responsável legal</strong> conforme a <strong>LGPD (Lei 13.709/2018)</strong> para fins educacionais e administrativos.</span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Summary */}
                <div className="ac-aluno-wizard-resumo" style={{ marginTop: 0 }}>
                  <h4 className="ac-aluno-wizard-resumo-title">Resumo da Matrícula</h4>
                  <dl className="ac-aluno-wizard-resumo-dl">
                    <div><dt>Nome</dt><dd>{form.nome}</dd></div>
                    <div><dt>Matrícula</dt><dd>{form.matricula || 'Automática'}</dd></div>
                    <div><dt>Nascimento</dt><dd>{formatDateBR(form.dataNascimento)}</dd></div>
                    <div><dt>Gênero</dt><dd>{form.genero}</dd></div>
                    <div><dt>Turma</dt><dd>{turmas.find(t => t.id === form.turmaId)?.nome || '—'}</dd></div>
                    {form.cpf && <div><dt>CPF</dt><dd>{form.cpf}</dd></div>}
                    {form.corRaca && <div><dt>Cor/Raça</dt><dd>{form.corRaca}</dd></div>}
                    {form.nacionalidade && <div><dt>Nacionalidade</dt><dd>{form.nacionalidade.replace('_', ' ')}</dd></div>}
                    {form.paisOrigem && <div><dt>País</dt><dd>{form.paisOrigem}</dd></div>}
                    {form.nis && <div><dt>NIS</dt><dd>{form.nis}</dd></div>}
                    {responsaveis.filter(r => r.nome).map((r, i) => (
                      <div key={i}><dt>Resp. {i + 1}</dt><dd>{r.nome} ({r.parentesco || '—'})</dd></div>
                    ))}
                    {form.rfidCodigo && <div><dt>RFID</dt><dd>{form.rfidCodigo}</dd></div>}
                  </dl>
                </div>
              </div>
            </div>
          )}
        </div>

        {!editing && (
          <div className="modal-footer ac-aluno-wizard-footer">
            {step > 0 ? (
              <button
                id="aluno-form-btn-voltar"
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setStep((s) => s - 1);
                  setTimeout(() => { modalBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }, 50);
                }}
              >
                ← Voltar
              </button>
            ) : <div />}
            {wizardPrimaryAction}
          </div>
        )}
    </>
  ) : null;

  return (
    <>
      {editing ? (
        <Modal
          open={open && !successData}
          onClose={onClose}
          title="Editar Aluno"
          size="wide"
          contentClassName="ac-responsavel-modal ac-aluno-modal ac-aluno-modal--edit"
          preventClose
          dialogId="modal-ficha-aluno"
          footer={editModalFooter}
        >
          <img
            src="/icons/logo-shield.svg"
            alt="Aluno Conectado"
            style={{ position: 'absolute', top: 6, right: 10, width: 26, height: 26, opacity: 0.4, pointerEvents: 'none', zIndex: 2 }}
          />
          <div className="ac-modal-dual ac-aluno-edit-dual">
            {editPhotoBlock}
            <div className="ac-modal-dual__columns">
              <div className="ac-modal-dual__main ac-aluno-form-main">
                {wizardUI}
              </div>
              <div className="ac-modal-dual__divider ac-responsavel-modal-divider" aria-hidden />
              {editing?.id && escolaId && (
                <AlunoEditSidePanel
                  escolaId={escolaId}
                  alunoId={editing.id}
                  aluno={alunoDetalhe ?? editing}
                  turmaIdForm={form.turmaId}
                  turmas={turmas}
                  responsaveis={responsaveis}
                  loading={loadingAlunoDetalhe}
                />
              )}
            </div>
          </div>
        </Modal>
      ) : (
        <div
          className="modal-overlay"
          style={successData ? { display: 'none' } : undefined}
          data-dialog-id="modal-novo-aluno"
        >
          <div className="modal-content ac-aluno-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Matricular Aluno</h2>
              <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
            </div>
            {matriculaDraftsUI ?? wizardUI}
          </div>
        </div>
      )}

      {postWizardModals}
    </>
  );
}
